import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';

interface Candidate {
  id: string;
  candidate_code: string;
  full_name: string;
  email: string;
  applied_position: string;
  status: string;
  application_date: string;
}

const fetchDepartmentOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get(`/masters/departments?search=${encodeURIComponent(search)}`);
  return res.data.departments.map((d: any) => ({ value: d.id, label: `${d.name} (${d.code})` }));
};

export default function Candidates() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobileNumber: '',
    appliedPosition: '',
    source: '',
  });
  const [hireTarget, setHireTarget] = useState<Candidate | null>(null);
  const [hireForm, setHireForm] = useState({ employeeCode: '', departmentId: '', startDate: '' });
  const [hireResult, setHireResult] = useState<{ loginEmail: string; tempPassword: string } | null>(
    null
  );

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hr/candidates');
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [review, setReview] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post('/hr/candidates', formData);
      setFormData({ fullName: '', email: '', mobileNumber: '', appliedPosition: '', source: '' });
      setShowForm(false);
      setPortalUrl(res.data.portalUrl || null);
      fetchCandidates();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create candidate profile');
    }
  };

  const openHireModal = async (candidate: Candidate) => {
    const res = await api.get(`/hr/candidates/${candidate.id}/review`);
    setReview(res.data);
    setHireTarget(candidate);
    setHireForm({ employeeCode: '', departmentId: '', startDate: '' });
  };

  const handleHire = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hireTarget) return;
    try {
      const res = await api.put(`/hr/candidates/${hireTarget.id}/approve`, { ...hireForm, force: !review?.isComplete });
      setHireResult({ loginEmail: res.data.loginEmail, tempPassword: res.data.tempPassword });
      setHireTarget(null);
      fetchCandidates();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to approve candidate');
    }
  };

  const handleReject = async (candidate: Candidate) => {
    if (!window.confirm(`Reject ${candidate.full_name}?`)) return;
    await api.put(`/hr/candidates/${candidate.id}/reject`);
    fetchCandidates();
  };

  const columns: Column<Candidate>[] = [
    { key: 'candidate_code', header: 'Code' },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'email', header: 'Email' },
    { key: 'applied_position', header: 'Applied Position' },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'application_date',
      header: 'Application Date',
      render: (row) => new Date(row.application_date).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) =>
        row.status === 'approved' || row.status === 'rejected' ? (
          <span className="text-gray-400 text-sm capitalize">{row.status}</span>
        ) : (
          <div className="flex gap-3">
            <button onClick={() => openHireModal(row)} className="text-green-700 hover:underline text-sm font-medium">
              Review / Approve
            </button>
            <button onClick={() => handleReject(row)} className="text-red-600 hover:underline text-sm font-medium">
              Reject
            </button>
          </div>
        ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Onboarding - Candidates</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Add Candidate'}</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
              />
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Input
                type="tel"
                placeholder="Mobile Number"
                value={formData.mobileNumber}
                onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
              />
              <Input
                placeholder="Applied Position"
                value={formData.appliedPosition}
                onChange={(e) => setFormData({ ...formData, appliedPosition: e.target.value })}
              />
              <Select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                placeholder="Select Source"
                className="col-span-2"
                options={[
                  { value: 'LinkedIn', label: 'LinkedIn' },
                  { value: 'Referral', label: 'Referral' },
                  { value: 'Walk-in', label: 'Walk-in' },
                  { value: 'Job Board', label: 'Job Board' },
                  { value: 'Other', label: 'Other' },
                ]}
              />
            </div>
            <Button type="submit" className="w-full">
              Create Candidate Profile
            </Button>
          </form>
        </div>
      )}

      <Table columns={columns} rows={candidates} loading={loading} emptyMessage="No candidates yet" />

      <Modal isOpen={!!portalUrl} onClose={() => setPortalUrl(null)} title="Candidate Invited">
        <p className="text-sm text-gray-600 mb-2">The candidate portal link (also emailed):</p>
        <code className="block bg-gray-100 rounded p-2 text-xs break-all">{portalUrl}</code>
      </Modal>

      <Modal isOpen={!!hireTarget} onClose={() => setHireTarget(null)} title={`Hire ${hireTarget?.full_name}`}>
        {review && !review.isComplete && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
            Candidate has not submitted: <strong>{review.missing.join(', ')}</strong>. Approving now will
            proceed anyway (force).
          </div>
        )}
        <form onSubmit={handleHire} className="space-y-4">
          <Input
            label="Employee Code"
            value={hireForm.employeeCode}
            onChange={(e) => setHireForm({ ...hireForm, employeeCode: e.target.value })}
            required
          />
          <LookupSelect
            label="Department"
            value={hireForm.departmentId}
            onChange={(value) => setHireForm({ ...hireForm, departmentId: value })}
            fetchOptions={fetchDepartmentOptions}
            placeholder="Search department..."
          />
          <Input
            label="Start Date"
            type="date"
            value={hireForm.startDate}
            onChange={(e) => setHireForm({ ...hireForm, startDate: e.target.value })}
            required
          />
          <Button type="submit" className="w-full">
            Confirm Hire
          </Button>
        </form>
      </Modal>

      <Modal isOpen={!!hireResult} onClose={() => setHireResult(null)} title="Employee Account Created">
        <p className="text-gray-600 mb-4">
          Share these login credentials with the new employee — they should change the password after
          first login.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <p className="text-sm">
            <span className="font-semibold">Email:</span> {hireResult?.loginEmail}
          </p>
          <p className="text-sm">
            <span className="font-semibold">Temporary Password:</span>{' '}
            <code className="bg-gray-200 px-2 py-1 rounded">{hireResult?.tempPassword}</code>
          </p>
        </div>
      </Modal>
    </div>
  );
}
